/**
 * Event Bus centralizado
 * Permite comunicación entre módulos sin acoplamiento directo
 * 
 * Patrones:
 * - emit(eventName, payload): Dispara evento
 * - on(eventName, callback): Suscribirse a evento
 * - once(eventName, callback): Suscribirse a evento una sola vez
 * - off(eventName, callback): Desuscribirse
 */

class EventBus {
  constructor() {
    // Map de eventName -> Set de callbacks
    this.#listeners = new Map();
    this.#history = []; // Para debugging
  }

  // Almacenamiento privado de listeners
  #listeners;
  #history;

  /**
   * Dispara un evento
   * @param {string} eventName - Nombre del evento
   * @param {*} payload - Datos a pasar a los listeners
   */
  emit(eventName, payload) {
    if (!this.#listeners.has(eventName)) {
      return;
    }

    const listeners = this.#listeners.get(eventName);
    listeners.forEach(callback => {
      try {
        callback(payload);
      } catch (error) {
        console.error(`Error en listener de evento "${eventName}":`, error);
      }
    });

    // Guardar en historial para debugging
    this.#history.push({
      event: eventName,
      timestamp: new Date(),
      payload
    });

    // Mantener historial limitado
    if (this.#history.length > 100) {
      this.#history.shift();
    }
  }

  /**
   * Suscribirse a un evento
   * @param {string} eventName - Nombre del evento
   * @param {Function} callback - Función a ejecutar
   * @returns {Function} Función para desuscribirse
   */
  on(eventName, callback) {
    if (!this.#listeners.has(eventName)) {
      this.#listeners.set(eventName, new Set());
    }

    const listeners = this.#listeners.get(eventName);
    listeners.add(callback);

    // Retornar función de desuscripción
    return () => {
      listeners.delete(callback);
      if (listeners.size === 0) {
        this.#listeners.delete(eventName);
      }
    };
  }

  /**
   * Suscribirse a un evento una sola vez
   * @param {string} eventName - Nombre del evento
   * @param {Function} callback - Función a ejecutar
   * @returns {Function} Función para desuscribirse
   */
  once(eventName, callback) {
    const unsubscribe = this.on(eventName, (payload) => {
      callback(payload);
      unsubscribe();
    });
    return unsubscribe;
  }

  /**
   * Desuscribirse de un evento
   * @param {string} eventName - Nombre del evento
   * @param {Function} callback - Función a remover
   */
  off(eventName, callback) {
    if (!this.#listeners.has(eventName)) {
      return;
    }

    const listeners = this.#listeners.get(eventName);
    listeners.delete(callback);

    if (listeners.size === 0) {
      this.#listeners.delete(eventName);
    }
  }

  /**
   * Obtener historial de eventos (para debugging)
   * @returns {Array} Historial de eventos
   */
  getHistory() {
    return [...this.#history];
  }

  /**
   * Limpiar historial
   */
  clearHistory() {
    this.#history = [];
  }
}

// Singleton global
const eventBus = new EventBus();

/**
 * Eventos predefinidos de la aplicación
 * Se documentan aquí como constantes para evitar typos
 */
export const EVENTS = {
  // Aplicación
  'app:ready': 'app:ready', // La aplicación está lista
  'app:error': 'app:error', // Error en la aplicación

  // Módulos
  'module:loaded': 'module:loaded', // Módulo cargado
  'module:changed': 'module:changed', // Usuario cambió de módulo

  // Conexión
  'online:connected': 'online:connected', // Conexión restaurada
  'offline:disconnected': 'offline:disconnected', // Se perdió conexión

  // Sincronización
  'sync:started': 'sync:started', // Inicio de sincronización
  'sync:completed': 'sync:completed', // Sincronización completada
  'sync:error': 'sync:error', // Error en sincronización
  'sync:conflict': 'sync:conflict', // Conflicto detectado

  // Ingredientes
  'ingrediente:created': 'ingrediente:created',
  'ingrediente:updated': 'ingrediente:updated',
  'ingrediente:deleted': 'ingrediente:deleted',
  'ingredientes:loaded': 'ingredientes:loaded',

  // Recetas
  'receta:created': 'receta:created',
  'receta:updated': 'receta:updated',
  'receta:deleted': 'receta:deleted',
  'recetas:loaded': 'recetas:loaded',

  // Auth
  'auth:signedIn': 'auth:signedIn',
  'auth:signedOut': 'auth:signedOut',

  // Ventas
  'ventas:loaded': 'ventas:loaded',

  // Stock / Movements
  'stock:loaded': 'stock:loaded',
  'stock:lowAlert': 'stock:lowAlert',

  // Productos
  'producto:created': 'producto:created',
  'producto:updated': 'producto:updated',
  'producto:deleted': 'producto:deleted',
  'productos:loaded': 'productos:loaded',

  // UI
  'ui:loading': 'ui:loading',
  'ui:loaded': 'ui:loaded',
  'notification:show': 'notification:show',
};

export default eventBus;
